import { createReducer, on } from '@ngrx/store';
import { IdentifiedAssistEvent } from '../models';
import { calendarSyncTimelineEventsPatched, refreshSchedules, schedulesLoaded } from '../actions/schedules.actions';
import { Pack } from '@cue/data';
const initialState: Pack<IdentifiedAssistEvent[]> = {
  loading: false,
  data: [],
  error: null,
};

function rangesOverlap(startA: Date, endA: Date, startB: Date, endB: Date): boolean {
  return startA < endB && startB < endA;
}

function getEventIdentity(event: IdentifiedAssistEvent): string {
  return [
    event.iCalUId,
    event.occurrenceId ?? event.start.utcDateTime.toISOString(),
    event.end.utcDateTime.toISOString(),
    event.organizer?.emailAddress?.address ?? '',
  ]
    .map((part) => String(part).trim().toLowerCase())
    .join('|');
}

function mergePatchedEvents(events: IdentifiedAssistEvent[]): IdentifiedAssistEvent[] {
  const byIdentity = new Map<string, IdentifiedAssistEvent>();

  for (const event of events) {
    const key = getEventIdentity(event);
    const existing = byIdentity.get(key);

    if (!existing) {
      byIdentity.set(key, event);
      continue;
    }

    byIdentity.set(key, {
      ...existing,
      resourceAttendees: [
        ...existing.resourceAttendees,
        ...event.resourceAttendees.filter(
          (incoming) => !existing.resourceAttendees.some((current) => current.resource.id === incoming.resource.id)
        ),
      ],
    });
  }

  return [...byIdentity.values()];
}

export const myEventsReducer = createReducer(
  initialState,
  on(refreshSchedules, (state) => ({
    ...state,
    loading: true,
    data: [],
  })),
  on(schedulesLoaded, (state, { myEvents }) => ({
    ...state,
    loading: false,
    data: myEvents,
  })),
  on(calendarSyncTimelineEventsPatched, (state, { events, start, end, resourceUsernames }) => {
    const affectedUsernames = new Set(resourceUsernames.map((username) => username.toLowerCase()));

    const retainedEvents = state.data
      .map((event) => {
        if (!rangesOverlap(event.start.utcDateTime, event.end.utcDateTime, start, end)) {
          return event;
        }

        const affectsEvent = event.resourceAttendees.some(
          (attendee) => attendee.resource.username && affectedUsernames.has(attendee.resource.username.toLowerCase())
        );

        if (!affectsEvent) {
          return event;
        }

        const retainedResourceAttendees = event.resourceAttendees.filter(
          (attendee) => !attendee.resource.username || !affectedUsernames.has(attendee.resource.username.toLowerCase())
        );

        if (retainedResourceAttendees.length === 0) {
          return null;
        }

        return {
          ...event,
          resourceAttendees: retainedResourceAttendees,
        };
      })
      .filter((event): event is IdentifiedAssistEvent => event != null);

    return {
      ...state,
      loading: false,
      data: mergePatchedEvents([...retainedEvents, ...events]),
    };
  })
);
